import type { AppointmentScheduledMessage } from "../types/appointmentScheduledMessages";
import { MESSAGE_TYPE_LABELS } from "../types/messageTemplates";

/**
 * Presentation helpers for appointment scheduled messages, shared by the
 * Transaction Modal's Scheduled Reminders panel and the two-way chat
 * "Scheduled messages" drawer so status colors, labels, and the
 * actionable-vs-terminal status buckets can't drift between the two surfaces.
 */

export const STATUS_COLORS: Record<string, string> = {
  SCHEDULED: "blue",
  QUEUED: "cyan",
  SENDING: "yellow",
  SENT: "green",
  FAILED: "red",
  CANCELLED: "gray",
  // SKIPPED: dispatcher's compliance re-check failed at fire time (e.g. all
  // required forms were completed, client opted out, etc.).
  SKIPPED: "gray",
  // CHANGED: send time was moved into the salon's quiet-hours window; the
  // message still fires at the adjusted time.
  CHANGED: "burntOrange",
};

// Friendly explanations shown under a row, replacing the raw backend
// failure_reason (which can be terse / camelcase / enum-shaped). SKIPPED is
// distinct from FAILED — expected dispatcher behavior, not a delivery error.
export const SKIPPED_REASON =
  "Skipped at send time — the message was no longer needed (e.g. all required forms completed) or the client opted out.";

export const CHANGED_REASON =
  "Send time was moved into the salon's allowed messaging hours (8am–9pm). It will still send at the adjusted time.";

export const CANCELLED_REASON = "Cancelled by staff";

// Statuses for which the dispatcher will still send the message — staff can
// "Send Now" / "Cancel" these. CHANGED is pending-but-rescheduled. SENDING is
// intentionally excluded: the consumer has already claimed the row and a
// "Send Now" click would race the in-flight send.
export const PENDING_STATUSES = new Set<AppointmentScheduledMessage["status"]>([
  "SCHEDULED",
  "QUEUED",
  "CHANGED",
]);

export const TERMINAL_STATUSES = new Set<AppointmentScheduledMessage["status"]>(
  ["SENT", "FAILED", "CANCELLED", "SKIPPED"],
);

export const POST_APPOINTMENT_DEFINITION_KEY = "post_appointment.message";

// Labels for definition_key-driven rows (bypass MESSAGE_TYPE_LABELS).
const DEFINITION_KEY_LABELS: Record<string, string> = {
  "post_appointment.message": "Post-Appointment Message",
};

function getMessageTypeLabel(messageType?: string | null): string {
  if (!messageType) return "Scheduled Message";
  return (
    (MESSAGE_TYPE_LABELS as Record<string, string>)[messageType] ??
    messageType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

/**
 * Resolves the display name shown to staff for a scheduled message. Prefers the
 * salon-configured template title; falls back to a definition_key label, then
 * the human-readable message_type label so toasts never read "null has been
 * sent" and the list never shows a bare enum key.
 */
export function getScheduledMessageDisplayName(
  msg: Pick<AppointmentScheduledMessage, "template_title" | "message_type"> & {
    definition_key?: string | null;
  },
): string {
  const title = msg.template_title?.trim();
  if (title) return title;
  if (msg.definition_key && DEFINITION_KEY_LABELS[msg.definition_key]) {
    return DEFINITION_KEY_LABELS[msg.definition_key];
  }
  return getMessageTypeLabel(msg.message_type);
}
